
import { Box, Stack, Typography } from "@mui/material";
import SectionTabs from "../SectionTabs";
import CompositionHeader from "../CompositionHeader";
import { PatientCard, PractitionerCard, OrganizationSection, HospitalBanner } from "../InfoCards";
import EncounterSection from "../EncounterSection";
import DocumentReferenceSection from "../DocumentReferenceSection";
import { labelSx, valueSx, pillSx, formatDate } from "../styles";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ConsultationView = ({ bundle, setShowPdfPreview }: { bundle: any; setShowPdfPreview: (v: boolean) => void }) => {
    const composition = bundle?.composition || {};
    const patient = bundle || {};
    const allergies = bundle?.allergyIntolerances || [];
    const medications = bundle?.medicationRequest || [];
    const procedures = bundle?.procedures || [];
    const observations = bundle?.observations || [];
    const conditions = bundle?.conditions || [];

    const sections = [
        {
            id: "medications",
            label: "Medications",
            content:
                medications.length === 0 ? (
                    <Typography sx={labelSx}>No medications recorded.</Typography>
                ) : (
                    <Stack spacing={1.5}>
                        {medications.map((med: any, idx: number) => (
                            <Box
                                key={`med-${idx}`}
                                sx={{
                                    p: 2,
                                    border: "2px solid #f0f0f0",
                                    borderRadius: 2.5,
                                    bgcolor: "linear-gradient(135deg, #fafafa 0%, #f5fffe 100%)",
                                    boxShadow: "0 4px 16px rgba(129,196,187,0.12)",
                                    transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
                                    "&:hover": {
                                        boxShadow: "0 8px 24px rgba(129,196,187,0.2)",
                                        borderColor: "#2fbf71",
                                        bgcolor: "#fff",
                                        transform: "translateY(-2px)"
                                    }
                                }}
                            >
                                <Typography sx={{ ...valueSx, fontWeight: 600, fontSize: "1rem", color: "#1f3a2e" }}>
                                    {med.medicationName || "Medication"}
                                </Typography>
                                <Box
                                    sx={{
                                        mt: 1.5,
                                        display: "grid",
                                        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" },
                                        gap: 2
                                    }}
                                >
                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                        <Typography sx={labelSx}>Status</Typography>
                                        <Typography sx={{ ...pillSx, bgcolor: "#2fbf71", color: "#ffffff" }}>
                                            {med.medicationStatus || "—"}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                        <Typography sx={labelSx}>Dosage</Typography>
                                        <Typography sx={valueSx}>{med.dosageInstruction || "—"}</Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                        <Typography sx={labelSx}>Route</Typography>
                                        <Typography sx={valueSx}>{med.dosageRoute || "—"}</Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                        <Typography sx={labelSx}>Frequency</Typography>
                                        <Typography sx={valueSx}>{med.dosageFrequency} times per {med.dosagePeriod} {med.dosagePeriodUnit || "day"}</Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                        <Typography sx={labelSx}>Prescribed By</Typography>
                                        <Typography sx={valueSx}>{med.requester || "—"}</Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                        <Typography sx={labelSx}>Date Prescribed</Typography>
                                        <Typography sx={valueSx}>{formatDate(med.datePrescribed)}</Typography>
                                    </Box>
                                </Box>
                                {med.dosageAdditionalInstructions && med.dosageAdditionalInstructions.length > 0 && (
                                    <Box sx={{ mt: 1.5, p: 1.5, bgcolor: "#f5f5f5", borderRadius: 1 }}>
                                        <Typography sx={labelSx}>Additional Instructions</Typography>
                                        <Typography sx={valueSx}>{med.dosageAdditionalInstructions.join(", ")}</Typography>
                                    </Box>
                                )}
                            </Box>
                        ))}
                    </Stack>
                )
        },
        {
            id: "conditions",
            label: "Conditions",
            content:
                conditions.length === 0 ? (
                    <Typography sx={labelSx}>No conditions recorded.</Typography>
                ) : (
                    <Stack spacing={1.5}>
                        {conditions.map((condition: any, idx: number) => (
                            <Box
                                key={`condition-${idx}`}
                                sx={{
                                    p: 2,
                                    border: "2px solid #f0f0f0",
                                    borderRadius: 2.5,
                                    bgcolor: "linear-gradient(135deg, #fafafa 0%, #f5fffe 100%)",
                                    boxShadow: "0 4px 16px rgba(129,196,187,0.12)",
                                    transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
                                    "&:hover": {
                                        boxShadow: "0 8px 24px rgba(129,196,187,0.2)",
                                        borderColor: "#2fbf71",
                                        bgcolor: "#fff",
                                        transform: "translateY(-2px)"
                                    }
                                }}
                            >
                                <Typography sx={{ ...valueSx, fontWeight: 600, fontSize: "1rem", color: "#1f3a2e" }}>
                                    {condition.conditionText || "Condition"}
                                </Typography>
                                <Box
                                    sx={{
                                        mt: 1.5,
                                        display: "grid",
                                        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                                        gap: 2
                                    }}
                                >
                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                        <Typography sx={labelSx}>Type</Typography>
                                        <Typography sx={valueSx}>{condition.conditionType || "—"}</Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                        <Typography sx={labelSx}>Recorded Date</Typography>
                                        <Typography sx={valueSx}>{formatDate(condition.recordedDate)}</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        ))}
                    </Stack>
                )
        },
        {
            id: "procedures",
            label: "Procedures",
            content:
                procedures.length === 0 ? (
                    <Typography sx={labelSx}>No procedures recorded.</Typography>
                ) : (
                    <Stack spacing={1.5}>
                        {procedures.map((procedure: any, idx: number) => (
                            <Box
                                key={`procedure-${idx}`}
                                sx={{
                                    p: 2,
                                    border: "2px solid #f0f0f0",
                                    borderRadius: 2.5,
                                    bgcolor: "linear-gradient(135deg, #fafafa 0%, #f5fffe 100%)",
                                    boxShadow: "0 4px 16px rgba(129,196,187,0.12)",
                                    transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
                                    "&:hover": {
                                        boxShadow: "0 8px 24px rgba(129,196,187,0.2)",
                                        borderColor: "#2fbf71",
                                        bgcolor: "#fff",
                                        transform: "translateY(-2px)"
                                    }
                                }}
                            >
                                <Typography sx={{ ...valueSx, fontWeight: 600, fontSize: "1rem", color: "#1f3a2e" }}>
                                    {procedure.procedureText || "Procedure"}
                                </Typography>
                                <Box
                                    sx={{
                                        mt: 1.5,
                                        display: "grid",
                                        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                                        gap: 2
                                    }}
                                >
                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                        <Typography sx={labelSx}>Status</Typography>
                                        <Typography sx={{ ...pillSx, bgcolor: procedure.status === "completed" ? "#e8f5e9" : "#fff3e0", color: procedure.status === "completed" ? "#2e7d32" : "#e65100" }}>
                                            {procedure.status || "—"}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                        <Typography sx={labelSx}>Performed Date</Typography>
                                        <Typography sx={valueSx}>{formatDate(procedure.performedDate)}</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        ))}
                    </Stack>
                )
        },
        {
            id: "observations",
            label: "Observations",
            content:
                observations.length === 0 ? (
                    <Typography sx={labelSx}>No observations recorded.</Typography>
                ) : (
                    <Stack spacing={1.5}>
                        {observations.map((obs: any, idx: number) => (
                            <Box
                                key={`obs-${idx}`}
                                sx={{
                                    p: 2,
                                    border: "2px solid #f0f0f0",
                                    borderRadius: 2.5,
                                    bgcolor: "linear-gradient(135deg, #fafafa 0%, #f5fffe 100%)",
                                    boxShadow: "0 4px 16px rgba(129,196,187,0.12)",
                                    transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
                                    "&:hover": {
                                        boxShadow: "0 8px 24px rgba(129,196,187,0.2)",
                                        borderColor: "#2fbf71",
                                        bgcolor: "#fff",
                                        transform: "translateY(-2px)"
                                    }
                                }}
                            >
                                <Typography sx={{ ...valueSx, fontWeight: 600, fontSize: "1rem", color: "#1f3a2e" }}>
                                    {obs.observationDisplay || "Observation"}
                                </Typography>
                                <Box
                                    sx={{
                                        mt: 1.5,
                                        display: "grid",
                                        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" },
                                        gap: 2
                                    }}
                                >
                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                        <Typography sx={labelSx}>Status</Typography>
                                        <Typography sx={{ ...pillSx, bgcolor: "#e3f2fd", color: "#1565c0" }}>
                                            {obs.status || "—"}
                                        </Typography>
                                    </Box>
                                    {obs.value && (
                                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                                            <Typography sx={labelSx}>Value</Typography>
                                            <Typography sx={valueSx}>
                                                {obs.value} {obs.unitValue || ""}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                                {obs.noteTexts && obs.noteTexts.length > 0 && (
                                    <Box sx={{ mt: 1.5, p: 1.5, bgcolor: "#f5f5f5", borderRadius: 1 }}>
                                        <Typography sx={labelSx}>Notes</Typography>
                                        {obs.noteTexts.map((note: string, noteIdx: number) => (
                                            <Typography key={noteIdx} sx={valueSx}>• {note}</Typography>
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        ))}
                    </Stack>
                )
        },
        {
            id: "allergies",
            label: "Allergies",
            content:
                allergies.length === 0 ? (
                    <Typography sx={labelSx}>No allergies recorded.</Typography>
                ) : (
                    <Stack spacing={1.5}>
                        {allergies.map((allergy: any, idx: number) => (
                            <Box
                                key={`allergy-${idx}`}
                                sx={{
                                    p: 2,
                                    border: "2px solid #f0f0f0",
                                    borderRadius: 2.5,
                                    bgcolor: "linear-gradient(135deg, #fafafa 0%, #f5fffe 100%)",
                                    boxShadow: "0 4px 16px rgba(129,196,187,0.12)",
                                    transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
                                    "&:hover": {
                                        boxShadow: "0 8px 24px rgba(129,196,187,0.2)",
                                        borderColor: "#2fbf71",
                                        bgcolor: "#fff",
                                        transform: "translateY(-2px)"
                                    }
                                }}
                            >
                                <Typography sx={valueSx}>{allergy.allergyDisplay || "Allergy"}</Typography>
                                <Box
                                    sx={{
                                        mt: 0.5,
                                        display: "grid",
                                        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" },
                                        gap: 2
                                    }}
                                >
                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                        <Typography sx={labelSx}>Clinical Status</Typography>
                                        <Typography sx={valueSx}>{allergy.clinicalStatus || "—"}</Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                        <Typography sx={labelSx}>Verification Status</Typography>
                                        <Typography sx={valueSx}>{allergy.verificationStatus || "—"}</Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                        <Typography sx={labelSx}>Authored</Typography>
                                        <Typography sx={valueSx}>{formatDate(allergy.authoredDate)}</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        ))}
                    </Stack>
                )
        },
        {
            id: "attachments",
            label: "Attachments",
            content: bundle?.documentReference || bundle?.binary?.data ? (
                <DocumentReferenceSection
                    documentReference={bundle?.documentReference}
                    binary={bundle?.binary}
                    onPreviewBase64={() => setShowPdfPreview(true)}
                />
            ) : (
                <Typography sx={labelSx}>No attachments available.</Typography>
            )
        }
    ];

    return (
        <Stack spacing={3}>
            <HospitalBanner organization={bundle?.organization} />
            <CompositionHeader composition={composition} />

            <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mt: "8px !important" }}>
                <PatientCard patient={patient} />
                <PractitionerCard practitioner={patient} />
            </Stack>

            <OrganizationSection organization={bundle?.organization} />
            <EncounterSection encounter={bundle?.encounter} />
            <SectionTabs sections={sections} initialId="medications" />
        </Stack>
    );
};

export default ConsultationView;
